import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, Copy, Download } from "lucide-react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const INPUT = "w-full px-3 py-2.5 rounded-xl border border-[#D5CCBC] bg-white text-sm text-[#1E2B1D] focus:outline-none focus:ring-2 focus:ring-[#3D5A40]";

export default function RecoveryCodes() {
  const [status, setStatus] = useState(null);
  const [password, setPassword] = useState("");
  const [codes, setCodes] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/auth/recovery-status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const generate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/recovery-codes", { current_password: password });
      setCodes(data.codes);
      setPassword("");
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Gagal membuat kode pemulihan");
    } finally {
      setBusy(false);
    }
  };

  const text = () => `Kode Pemulihan Admin Puspita Florist\nDibuat: ${new Date().toLocaleString("id-ID")}\nSetiap kode hanya bisa dipakai 1x.\n\n${codes.join("\n")}\n`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(codes.join("\n"));
      toast.success("Kode disalin");
    } catch {
      toast.error("Gagal menyalin, silakan unduh");
    }
  };
  const download = () => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([text()], { type: "text/plain;charset=utf-8" }));
    a.download = "kode-pemulihan-puspita-florist.txt";
    a.click();
  };

  const ok = status?.remaining > 0;

  return (
    <div data-testid="recovery-codes" className="bg-[#FBF9F5] rounded-2xl border border-[#E8E2D5] p-6 space-y-4">
      <div className="flex items-center gap-2">
        {ok ? <ShieldCheck className="w-4 h-4 text-[#3D5A40]" /> : <ShieldAlert className="w-4 h-4 text-[#B5651D]" />}
        <span className="text-sm font-bold text-[#1E2B1D]">Kode Pemulihan (Lupa Kata Sandi)</span>
      </div>
      <p className="text-xs text-[#6B7C66]">
        Kode rahasia sekali pakai untuk mengatur ulang kata sandi dari halaman login jika Anda lupa. Simpan di tempat aman — kode hanya ditampilkan satu kali saat dibuat.
      </p>
      {status && (
        <p data-testid="recovery-status" className={`text-xs font-semibold px-3 py-2 rounded-lg ${ok ? "bg-[#EAF2E9] text-[#2E4530]" : "bg-[#FBEBDD] text-[#7A4A1D]"}`}>
          {status.has_codes
            ? `${status.remaining} dari ${status.total} kode masih bisa dipakai${status.generated_at ? ` · dibuat ${new Date(status.generated_at).toLocaleDateString("id-ID")}` : ""}`
            : "Belum ada kode pemulihan. Buat sekarang agar akun tidak terkunci jika lupa kata sandi."}
        </p>
      )}
      <form onSubmit={generate} className="flex flex-col sm:flex-row gap-3 sm:items-end">
        <div className="flex-1 max-w-xs">
          <label className="block text-xs font-bold uppercase tracking-wider text-[#354634] mb-1.5">Kata Sandi Saat Ini</label>
          <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="recovery-password-input" className={INPUT} />
        </div>
        <button type="submit" disabled={busy} data-testid="recovery-generate-btn" className="px-5 py-2.5 rounded-xl bg-[#3D5A40] hover:bg-[#2E4530] disabled:opacity-50 text-[#FAF7EE] text-sm font-bold transition-colors">
          {busy ? "Membuat..." : status?.has_codes ? "Buat Kode Baru (ganti lama)" : "Buat Kode Pemulihan"}
        </button>
      </form>

      <Dialog open={!!codes} onOpenChange={(v) => !v && setCodes(null)}>
        <DialogContent className="max-w-md bg-[#FBF9F5]" data-testid="recovery-codes-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-xl">Simpan Kode Pemulihan Anda</DialogTitle>
            <DialogDescription className="text-xs text-[#5E6D5B]">
              Kode ini hanya ditampilkan sekali. Setiap kode berlaku satu kali pakai. Kode lama otomatis tidak berlaku.
            </DialogDescription>
          </DialogHeader>
          {codes && (
            <ul data-testid="recovery-codes-list" className="grid grid-cols-1 gap-2 my-2">
              {codes.map((c) => (
                <li key={c} className="font-mono text-base tracking-widest text-center bg-white border border-[#D5CCBC] rounded-lg py-2 text-[#1E2B1D]">{c}</li>
              ))}
            </ul>
          )}
          <div className="flex gap-2 justify-end">
            <button onClick={copy} data-testid="recovery-copy-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#D5CCBC] text-xs font-semibold text-[#3D5A40] hover:bg-[#F4EFE6]"><Copy className="w-3.5 h-3.5" /> Salin</button>
            <button onClick={download} data-testid="recovery-download-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#3D5A40] text-white text-xs font-bold hover:bg-[#2E4530]"><Download className="w-3.5 h-3.5" /> Unduh .txt</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
